import type { PerfectDrawProject } from "../projects/types";
import { validateLayerDocument } from "../layers/layerValidation";
import { clampBubbleToPanel } from "./BubbleGeometry";
import { sanitizeBubbleText } from "./BubbleTextLayout";
import { isPanelGeometryValid } from "./PanelGeometry";
import {
  COMIC_DOCUMENT_SCHEMA_VERSION,
  MAX_BUBBLE_TEXT_LENGTH,
  MAX_COMIC_OBJECTS,
  isComicBubbleType,
  isComicTemplateId,
  type ComicBubbleData,
  type ComicBubbleObject,
  type ComicPanel,
  type PersistedDocumentState,
} from "./comicTypes";

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;
const TEMPLATE_PANEL_COUNTS = {
  "one-panel": 1,
  "two-panel": 2,
  "three-panel": 3,
  "four-panel": 4,
  "six-panel": 6,
} as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function readBubbleData(value: unknown): ComicBubbleData {
  if (!isRecord(value)) throw new Error("Comic object data is invalid.");
  if (typeof value.text !== "string" || value.text.length > MAX_BUBBLE_TEXT_LENGTH) {
    throw new Error("Comic object text is invalid.");
  }
  if (
    !finite(value.fontSize) ||
    value.fontSize < 12 ||
    value.fontSize > 160 ||
    (value.textAlign !== "left" && value.textAlign !== "center") ||
    typeof value.fillColor !== "string" ||
    !HEX_COLOUR.test(value.fillColor) ||
    typeof value.strokeColor !== "string" ||
    !HEX_COLOUR.test(value.strokeColor) ||
    !finite(value.strokeWidth) ||
    value.strokeWidth < 0 ||
    value.strokeWidth > 24
  ) {
    throw new Error("Comic object style is invalid.");
  }
  const tailDirection = value.tailDirection;
  if (
    tailDirection !== undefined &&
    tailDirection !== "bottom-left" &&
    tailDirection !== "bottom-right" &&
    tailDirection !== "left" &&
    tailDirection !== "right"
  ) {
    throw new Error("Comic object tail is invalid.");
  }
  return {
    text: sanitizeBubbleText(value.text),
    fontSize: value.fontSize,
    textAlign: value.textAlign,
    fillColor: value.fillColor.toLowerCase(),
    strokeColor: value.strokeColor.toLowerCase(),
    strokeWidth: value.strokeWidth,
    tailDirection,
  };
}

function readPanel(value: unknown): ComicPanel {
  if (!isRecord(value) || typeof value.id !== "string" || value.id.length === 0) {
    throw new Error("Comic panel is invalid.");
  }
  const numeric = [value.x, value.y, value.width, value.height, value.order, value.templateSlot];
  if (!numeric.every(finite)) throw new Error("Comic panel geometry is invalid.");
  return {
    id: value.id,
    x: value.x as number,
    y: value.y as number,
    width: value.width as number,
    height: value.height as number,
    order: value.order as number,
    templateSlot: value.templateSlot as number,
  };
}

function readObject(
  value: unknown,
  panels: ReadonlyMap<string, ComicPanel>,
  layerIds: ReadonlySet<string>,
): ComicBubbleObject {
  if (
    !isRecord(value) ||
    typeof value.id !== "string" ||
    value.id.length === 0 ||
    !isComicBubbleType(value.type) ||
    typeof value.panelId !== "string" ||
    typeof value.layerId !== "string" ||
    !panels.has(value.panelId) ||
    !layerIds.has(value.layerId)
  ) {
    throw new Error("Comic object references are invalid.");
  }
  const numeric = [value.x, value.y, value.width, value.height, value.rotation, value.order];
  if (
    !numeric.every(finite) ||
    (value.width as number) <= 0 ||
    (value.height as number) <= 0 ||
    (value.rotation as number) < -180 ||
    (value.rotation as number) > 180
  ) {
    throw new Error("Comic object geometry is invalid.");
  }
  const panel = panels.get(value.panelId)!;
  const object: ComicBubbleObject = {
    id: value.id,
    type: value.type,
    panelId: value.panelId,
    layerId: value.layerId,
    x: value.x as number,
    y: value.y as number,
    width: value.width as number,
    height: value.height as number,
    rotation: value.rotation as number,
    order: value.order as number,
    data: readBubbleData(value.data),
  };
  const clamped = clampBubbleToPanel(object, panel);
  const epsilon = 0.01;
  if (
    Math.abs(clamped.x - object.x) > epsilon ||
    Math.abs(clamped.y - object.y) > epsilon ||
    Math.abs(clamped.width - object.width) > epsilon ||
    Math.abs(clamped.height - object.height) > epsilon ||
    Math.abs(clamped.rotation - object.rotation) > epsilon ||
    Math.abs(clamped.data.strokeWidth - object.data.strokeWidth) > epsilon
  ) {
    throw new Error("Comic object lies outside its panel.");
  }
  return clamped;
}

export function validatePersistedDocumentState(
  value: unknown,
  project: Pick<PerfectDrawProject, "type" | "width" | "height" | "layers" | "activeLayerId">,
): PersistedDocumentState {
  if (!isRecord(value) || value.schemaVersion !== COMIC_DOCUMENT_SCHEMA_VERSION) {
    throw new Error("Comic document schema is not supported.");
  }
  if (!Array.isArray(value.panels) || !Array.isArray(value.objects)) {
    throw new Error("Comic document structure is incomplete.");
  }
  const layerDocument = validateLayerDocument(value.layers, value.activeLayerId, project.type);
  if (value.objects.length > MAX_COMIC_OBJECTS) throw new Error("Comic object limit exceeded.");
  const templateId = value.comicTemplateId;
  if (project.type === "comic" && !isComicTemplateId(templateId)) {
    throw new Error("Comic template is invalid.");
  }
  if (project.type === "drawing" && templateId !== undefined) {
    throw new Error("Drawing projects cannot contain a comic template.");
  }
  const panels = value.panels.map(readPanel).sort((left, right) => left.order - right.order);
  if (project.type === "comic" && panels.length === 0) throw new Error("Comic panels are missing.");
  if (
    project.type === "comic" &&
    isComicTemplateId(templateId) &&
    panels.length !== TEMPLATE_PANEL_COUNTS[templateId]
  ) {
    throw new Error("Comic panels do not match the selected template.");
  }
  if (project.type === "drawing" && (panels.length > 0 || value.objects.length > 0)) {
    throw new Error("Drawing projects cannot contain comic document data.");
  }
  if (!isPanelGeometryValid(panels, project.width, project.height)) {
    throw new Error("Comic panel geometry is invalid.");
  }
  const panelMap = new Map(panels.map((panel) => [panel.id, panel]));
  const layerIds = new Set(
    layerDocument.layers.filter((layer) => layer.type === "object").map((layer) => layer.id),
  );
  const objectIds = new Set<string>();
  const objects = value.objects
    .map((object) => readObject(object, panelMap, layerIds))
    .sort((left, right) => left.order - right.order);
  for (const object of objects) {
    if (objectIds.has(object.id)) throw new Error("Comic object IDs must be unique.");
    objectIds.add(object.id);
  }
  return {
    schemaVersion: COMIC_DOCUMENT_SCHEMA_VERSION,
    activeLayerId: layerDocument.activeLayerId,
    layers: layerDocument.layers,
    comicTemplateId: isComicTemplateId(templateId) ? templateId : undefined,
    panels,
    objects,
  };
}

export function cloneDocumentState(state: PersistedDocumentState): PersistedDocumentState {
  return {
    schemaVersion: COMIC_DOCUMENT_SCHEMA_VERSION,
    activeLayerId: state.activeLayerId,
    layers: state.layers.map((layer) => ({ ...layer })),
    comicTemplateId: state.comicTemplateId,
    panels: state.panels.map((panel) => ({ ...panel })),
    objects: state.objects.map((object) => ({ ...object, data: { ...object.data } })),
  };
}
